/**
 * Pure role-assignment mapping for Test-Fit Studio imports.
 *
 * A parsed geometry row (KMZ / shapefile / GeoJSON / DWG via the shared
 * import pipeline) can become one of:
 *  - a REFERENCE overlay (site layer — zero replay surface),
 *  - a no-build EXCLUSION zone,
 *  - an acoustic BARRIER (required height),
 *  - a CUSTOM component (polygon footprint, or road corridor with a
 *    required width),
 *  - an EQUIPMENT pin (centroid + longest-edge bearing → manual placement).
 *
 * Everything except references lands on the frozen layout via ONE composed
 * patch (schemaVersion 2), so the server re-solve and publish replay see the
 * imported geometry deterministically. No wall clock, no randomness: ids
 * derive from a caller-supplied import id + row index.
 */
import {
  latLngForLocalAxes,
  localAxesForPoint,
} from '../../../../lib/generationImpact/generationLayoutGeometry';
import type { ParsedLayer } from '../../../../services/geoImport/geojsonToLayers';
import type {
  GenerationAcousticBarrier,
  GenerationCustomComponent,
  GenerationCustomComponentKind,
  GenerationImportedLayerAssignment,
  GenerationLayoutConfiguration,
  GenerationLayoutManualPlacement,
} from '../../../../types/generationImpact';
import { sha256Hex } from '../../../../lib/reportGeneration/reportContractHash';

export type StudioImportRole =
  | { role: 'reference' }
  | { role: 'exclusion' }
  | { role: 'barrier'; kind: GenerationAcousticBarrier['kind']; heightM: number | null }
  | { role: 'custom'; kind: GenerationCustomComponentKind; corridorWidthM: number | null }
  | { role: 'equipment'; componentId: string };

export interface StudioImportRow {
  layer: ParsedLayer;
  assignment: StudioImportRole;
}

export interface StudioImportApplication {
  /** Composed layout patch (schemaVersion 2), or null when nothing targets the layout. */
  patch: Partial<GenerationLayoutConfiguration> | null;
  /** Rows routed to the Site Map reference-overlay path. */
  referenceLayers: ParsedLayer[];
  /** Original staged indexes, parallel to `referenceLayers` (for CAD rows). */
  referenceRowIndexes: number[];
  /** Per-row validation errors (row retains its input; nothing partial lands). */
  errors: string[];
  /** Non-blocking advisories (e.g. near-square orientation ambiguity). */
  warnings: string[];
}

export interface EquipmentSlotOccupancy {
  kind: string;
  required: number;
  assigned: number;
  available: number;
  /** Component ids of this kind with no imported-source assignment yet. */
  unassignedComponentIds: string[];
  /** All component ids of this kind (for Replace target choice). */
  componentIds: string[];
}

/**
 * Occupancy per required-equipment kind: how many physical slots exist
 * (`required`), how many already hold an imported source (`assigned`), and
 * which remain (`available`). Required counts come from the solver's resolved
 * components (physical unit counts), never `input.units.length`.
 */
export function computeEquipmentOccupancy(
  components: ReadonlyArray<{ id: string; kind: string }>,
  importedAssignments: readonly GenerationImportedLayerAssignment[],
): EquipmentSlotOccupancy[] {
  const assignedComponentIds = new Set(
    importedAssignments
      .filter(assignment => assignment.role === 'equipment' && assignment.componentId)
      .map(assignment => assignment.componentId!),
  );
  const byKind = new Map<string, string[]>();
  for (const component of components) {
    const ids = byKind.get(component.kind) ?? [];
    ids.push(component.id);
    byKind.set(component.kind, ids);
  }
  return [...byKind.entries()].map(([kind, componentIds]) => {
    const unassignedComponentIds = componentIds.filter(id => !assignedComponentIds.has(id));
    return {
      kind,
      required: componentIds.length,
      assigned: componentIds.length - unassignedComponentIds.length,
      available: unassignedComponentIds.length,
      unassignedComponentIds,
      componentIds,
    };
  });
}

export interface GenerationEquipmentOccupancy {
  /** Packer component kind (e.g. 'generation-block', 'cooling'). */
  kind: string;
  /** Physical slots of this kind (generation blocks: the fleet count). */
  required: number;
  /** Slots already bound to an imported source. */
  assigned: number;
  /** Slots still free to accept a source. */
  available: number;
  /** Component ids of the free slots, in solver order. */
  unassignedComponentIds: string[];
}

/**
 * Per-kind occupancy for the assignment workflow: how many equipment slots
 * exist, how many an imported source already claims, and which remain free.
 * Drives the "3 required · 2 assigned · 1 available" readout and the
 * offer-an-unassigned-target-first rule. Counts physical slots (the component
 * list), never `input.units.length`.
 */
export function computeGenerationEquipmentOccupancy(
  components: ReadonlyArray<{ id: string; kind: string }>,
  importedAssignments: ReadonlyArray<GenerationImportedLayerAssignment>,
): GenerationEquipmentOccupancy[] {
  const assignedIds = new Set(
    importedAssignments
      .filter(assignment => assignment.role === 'equipment' && assignment.componentId)
      .map(assignment => assignment.componentId!),
  );
  const order: string[] = [];
  const byKind = new Map<string, string[]>();
  for (const component of components) {
    const ids = byKind.get(component.kind);
    if (ids) {
      ids.push(component.id);
    } else {
      byKind.set(component.kind, [component.id]);
      order.push(component.kind);
    }
  }
  return order.map(kind => {
    const ids = byKind.get(kind)!;
    const unassignedComponentIds = ids.filter(id => !assignedIds.has(id));
    return {
      kind,
      required: ids.length,
      assigned: ids.length - unassignedComponentIds.length,
      available: unassignedComponentIds.length,
      unassignedComponentIds,
    };
  });
}

/**
 * Remove every layout artifact owned by an imported source's assignment and
 * drop the assignment record — the source returns to Unassigned. Required
 * equipment (a manual placement) returns to the packer (i.e. Unplaced). Custom
 * geometry / barriers / exclusions are deleted. Returns null if unknown.
 */
export function unassignImportedSource(
  layout: GenerationLayoutConfiguration,
  assignmentId: string,
): Partial<GenerationLayoutConfiguration> | null {
  const assignmentRecords = layout.importedAssignments ?? [];
  if (!assignmentRecords.some(assignment => assignment.id === assignmentId)) return null;
  const owned = (artifact: { origin?: { assignmentId: string } }): boolean =>
    artifact.origin?.assignmentId === assignmentId;

  const patch: Partial<GenerationLayoutConfiguration> = { schemaVersion: 2 };
  const placements = layout.manualPlacements ?? [];
  if (placements.some(owned)) patch.manualPlacements = placements.filter(placement => !owned(placement));
  const customs = layout.customComponents ?? [];
  if (customs.some(owned)) patch.customComponents = customs.filter(component => !owned(component));
  const barriers = layout.acousticBarriers ?? [];
  if (barriers.some(owned)) patch.acousticBarriers = barriers.filter(barrier => !owned(barrier));
  const zones = layout.exclusions?.zones ?? [];
  if (layout.exclusions && zones.some(owned)) {
    patch.exclusions = { ...layout.exclusions, zones: zones.filter(zone => !owned(zone)) };
  }
  patch.importedAssignments = assignmentRecords.filter(assignment => assignment.id !== assignmentId);
  return patch;
}

/** Roles a row's geometry can actually take. A point can seed an equipment
 *  centre (bearing defaults to the layout axis, editable later). */
export function rolesForGeometry(type: ParsedLayer['geometry']['type']): StudioImportRole['role'][] {
  if (type === 'polygon') return ['reference', 'exclusion', 'barrier', 'custom', 'equipment'];
  if (type === 'line') return ['reference', 'barrier', 'custom'];
  return ['reference', 'equipment'];
}

/** Deterministic default role from the layer's name/category/geometry. */
export function suggestImportRole(layer: ParsedLayer): StudioImportRole {
  const name = `${layer.name} ${layer.folderPath.join(' ')}`.toLowerCase();
  const { type } = layer.geometry;
  if (type === 'polygon') {
    if (/setback|wetland|flood|no[- ]?build|exclusion|buffer/.test(name)
      || layer.category === 'constraint.environmental') {
      return { role: 'exclusion' };
    }
    if (/berm|sound\s*wall|noise\s*(?:wall|barrier)|acoustic/.test(name)) {
      return { role: 'barrier', kind: /berm/.test(name) ? 'berm' : 'building', heightM: null };
    }
    if (/parking/.test(name)) return { role: 'custom', kind: 'parking', corridorWidthM: null };
    if (/data\s*center|data\s*hall/.test(name)) return { role: 'custom', kind: 'data-center', corridorWidthM: null };
    if (/building|warehouse|admin/.test(name)) return { role: 'custom', kind: 'building', corridorWidthM: null };
    if (/generator|genset/.test(name)) return { role: 'custom', kind: 'generator', corridorWidthM: null };
    return { role: 'reference' };
  }
  if (type === 'line') {
    if (/road|access|drive|route/.test(name) || layer.category === 'infrastructure.transport') {
      return { role: 'custom', kind: 'road', corridorWidthM: null };
    }
    if (/berm/.test(name)) return { role: 'barrier', kind: 'berm', heightM: null };
    if (/wall|barrier|acoustic|noise/.test(name)) return { role: 'barrier', kind: 'wall', heightM: null };
    return { role: 'reference' };
  }
  return { role: 'reference' };
}

function openRing(coordinates: readonly { lat: number; lng: number }[]): Array<{ lat: number; lng: number }> {
  if (coordinates.length < 2) return [...coordinates];
  const first = coordinates[0]!;
  const last = coordinates.at(-1)!;
  return first.lat === last.lat && first.lng === last.lng
    ? coordinates.slice(0, -1)
    : [...coordinates];
}

/** World bearing (deg CCW from east) of the ring's longest edge, mod 180
 *  normalized into (-90, 90] for rectangle stability. */
export function longestEdgeBearingDeg(
  ring: readonly { lat: number; lng: number }[],
  anchor: { lat: number; lng: number },
): number {
  const open = openRing(ring);
  if (open.length < 2) return 0;
  let bestLengthSq = -1;
  let bearing = 0;
  for (let index = 0; index < open.length; index += 1) {
    const start = localAxesForPoint(open[index]!, anchor, 0);
    const end = localAxesForPoint(open[(index + 1) % open.length]!, anchor, 0);
    const dx = end.xM - start.xM;
    const dy = end.yM - start.yM;
    const lengthSq = dx * dx + dy * dy;
    if (lengthSq > bestLengthSq) {
      bestLengthSq = lengthSq;
      bearing = Math.atan2(dy, dx) * 180 / Math.PI;
    }
  }
  let normalized = bearing % 180;
  if (normalized > 90) normalized -= 180;
  if (normalized <= -90) normalized += 180;
  return normalized;
}

interface LocalPoint { xM: number; yM: number; }

/** Aspect ratio at/under which a source is "near-square" and its major axis
 *  is orientation-ambiguous, so the layout/target axis is used instead. */
export const NEAR_SQUARE_ASPECT = 1.05;

/** Normalize a bearing (deg) into (-90, 90] — a rectangle axis is mod 180. */
function normalizeAxisDeg(deg: number): number {
  let normalized = deg % 180;
  if (normalized > 90) normalized -= 180;
  if (normalized <= -90) normalized += 180;
  return normalized;
}

/**
 * Shoelace area-weighted centroid of the ring in the world-axis local frame
 * (rotation 0). Falls back to the vertex mean for degenerate/zero-area rings.
 */
function polygonCentroidLocal(points: readonly LocalPoint[]): LocalPoint | null {
  if (points.length === 0) return null;
  const mean = (): LocalPoint => {
    const sum = points.reduce((total, point) => ({ xM: total.xM + point.xM, yM: total.yM + point.yM }), { xM: 0, yM: 0 });
    return { xM: sum.xM / points.length, yM: sum.yM / points.length };
  };
  if (points.length < 3) return mean();
  let area = 0;
  let cx = 0;
  let cy = 0;
  for (let index = 0; index < points.length; index += 1) {
    const a = points[index]!;
    const b = points[(index + 1) % points.length]!;
    const cross = a.xM * b.yM - b.xM * a.yM;
    area += cross;
    cx += (a.xM + b.xM) * cross;
    cy += (a.yM + b.yM) * cross;
  }
  area *= 0.5;
  if (Math.abs(area) < 1e-9) return mean();
  return { xM: cx / (6 * area), yM: cy / (6 * area) };
}

/** Convex hull (Andrew's monotone chain) of local points, CCW, no repeat. */
function convexHull(points: readonly LocalPoint[]): LocalPoint[] {
  const sorted = [...points].sort((a, b) => a.xM - b.xM || a.yM - b.yM);
  if (sorted.length < 3) return sorted;
  const cross = (o: LocalPoint, a: LocalPoint, b: LocalPoint): number =>
    (a.xM - o.xM) * (b.yM - o.yM) - (a.yM - o.yM) * (b.xM - o.xM);
  const lower: LocalPoint[] = [];
  for (const point of sorted) {
    while (lower.length >= 2 && cross(lower[lower.length - 2]!, lower[lower.length - 1]!, point) <= 0) lower.pop();
    lower.push(point);
  }
  const upper: LocalPoint[] = [];
  for (let index = sorted.length - 1; index >= 0; index -= 1) {
    const point = sorted[index]!;
    while (upper.length >= 2 && cross(upper[upper.length - 2]!, upper[upper.length - 1]!, point) <= 0) upper.pop();
    upper.push(point);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

export interface MinAreaRectangle {
  /** Bearing of the LONGER side, deg CCW from east, in (-90, 90]. */
  bearingDeg: number;
  majorM: number;
  minorM: number;
  aspectRatio: number;
}

/**
 * Minimum-area bounding rectangle via rotating calipers on the convex hull.
 * The MAR of a rectangle recovers its true axis (unlike the longest single
 * edge on an irregular ring). Returns null for < 2 distinct points.
 */
export function minimumAreaRectangle(
  ring: readonly { lat: number; lng: number }[],
  anchor: { lat: number; lng: number },
): MinAreaRectangle | null {
  const hull = convexHull(openRing(ring).map(point => localAxesForPoint(point, anchor, 0)));
  if (hull.length < 2) return null;
  if (hull.length === 2) {
    const a = hull[0]!;
    const b = hull[1]!;
    const length = Math.hypot(b.xM - a.xM, b.yM - a.yM);
    return {
      bearingDeg: normalizeAxisDeg(Math.atan2(b.yM - a.yM, b.xM - a.xM) * 180 / Math.PI),
      majorM: length,
      minorM: 0,
      aspectRatio: Infinity,
    };
  }
  let best: { area: number; width: number; height: number; edgeAngle: number } | null = null;
  for (let index = 0; index < hull.length; index += 1) {
    const a = hull[index]!;
    const b = hull[(index + 1) % hull.length]!;
    const edgeAngle = Math.atan2(b.yM - a.yM, b.xM - a.xM);
    const cos = Math.cos(-edgeAngle);
    const sin = Math.sin(-edgeAngle);
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const point of hull) {
      const rx = point.xM * cos - point.yM * sin;
      const ry = point.xM * sin + point.yM * cos;
      minX = Math.min(minX, rx);
      maxX = Math.max(maxX, rx);
      minY = Math.min(minY, ry);
      maxY = Math.max(maxY, ry);
    }
    const width = maxX - minX;
    const height = maxY - minY;
    const area = width * height;
    if (best === null || area < best.area - 1e-9) {
      best = { area, width, height, edgeAngle };
    }
  }
  if (!best) return null;
  const major = Math.max(best.width, best.height);
  const minor = Math.min(best.width, best.height);
  // The width sits along the (rotated) x-axis; the major axis is that edge when
  // width is the longer side, otherwise the perpendicular.
  const majorAngle = best.width >= best.height ? best.edgeAngle : best.edgeAngle + Math.PI / 2;
  return {
    bearingDeg: normalizeAxisDeg(majorAngle * 180 / Math.PI),
    majorM: major,
    minorM: minor,
    aspectRatio: minor > 1e-9 ? major / minor : Infinity,
  };
}

/** Shoelace centroid of the ring returned as a lat/lng point. */
export function polygonCentroidLatLng(
  ring: readonly { lat: number; lng: number }[],
  anchor: { lat: number; lng: number },
): { lat: number; lng: number } | null {
  const centroid = polygonCentroidLocal(openRing(ring).map(point => localAxesForPoint(point, anchor, 0)));
  return centroid ? latLngForLocalAxes(centroid, anchor, 0) : null;
}

/** Absolute shoelace area of the ring in square metres (local frame). */
export function polygonAreaM2(
  ring: readonly { lat: number; lng: number }[],
  anchor: { lat: number; lng: number },
): number {
  const points = openRing(ring).map(point => localAxesForPoint(point, anchor, 0));
  if (points.length < 3) return 0;
  let area = 0;
  for (let index = 0; index < points.length; index += 1) {
    const a = points[index]!;
    const b = points[(index + 1) % points.length]!;
    area += a.xM * b.yM - b.xM * a.yM;
  }
  return Math.abs(area) / 2;
}

/** Above this per-axis difference (metres), the modeled envelope visibly
 *  overrides the imported size and the user must confirm before Apply. */
export const NORMALIZATION_CONFIRM_TOLERANCE_M = 0.01;

export interface EquipmentNormalizationPreflight {
  /** Source minimum-area-rectangle extents (metres). */
  sourceMajorM: number;
  sourceMinorM: number;
  sourceAreaM2: number;
  /** Authoritative modeled envelope (longer/shorter side, metres). */
  modeledLongM: number;
  modeledShortM: number;
  modeledAreaM2: number;
  /** Modeled ÷ source scale factors (what the render "resizes" the source by). */
  longScale: number;
  shortScale: number;
  areaScale: number;
  /** Derived major-axis bearing (deg CCW from east). */
  bearingDeg: number;
  nearSquare: boolean;
  /** True when either axis differs by more than the confirm tolerance. */
  needsConfirmation: boolean;
}

/**
 * Compare an imported source's footprint against the authoritative modeled
 * equipment envelope. The modeled dimensions always win — this only reports the
 * discrepancy and whether the user must confirm the override before Apply.
 */
export function equipmentNormalizationPreflight(
  ring: readonly { lat: number; lng: number }[],
  anchor: { lat: number; lng: number },
  modeledWidthM: number,
  modeledHeightM: number,
): EquipmentNormalizationPreflight | null {
  const rect = minimumAreaRectangle(ring, anchor);
  if (!rect) return null;
  const modeledLongM = Math.max(modeledWidthM, modeledHeightM);
  const modeledShortM = Math.min(modeledWidthM, modeledHeightM);
  const sourceAreaM2 = polygonAreaM2(ring, anchor);
  const modeledAreaM2 = modeledWidthM * modeledHeightM;
  const needsConfirmation =
    Math.abs(rect.majorM - modeledLongM) > NORMALIZATION_CONFIRM_TOLERANCE_M
    || Math.abs(rect.minorM - modeledShortM) > NORMALIZATION_CONFIRM_TOLERANCE_M;
  return {
    sourceMajorM: rect.majorM,
    sourceMinorM: rect.minorM,
    sourceAreaM2,
    modeledLongM,
    modeledShortM,
    modeledAreaM2,
    longScale: rect.majorM > 1e-9 ? modeledLongM / rect.majorM : Infinity,
    shortScale: rect.minorM > 1e-9 ? modeledShortM / rect.minorM : Infinity,
    areaScale: sourceAreaM2 > 1e-9 ? modeledAreaM2 / sourceAreaM2 : Infinity,
    bearingDeg: rect.bearingDeg,
    nearSquare: rect.aspectRatio <= NEAR_SQUARE_ASPECT,
    needsConfirmation,
  };
}

const MAX_BARRIER_HEIGHT_M = 50;
const MIN_BARRIER_HEIGHT_M = 0.5;

function importedComponentRef(
  layer: ParsedLayer,
  fallbackFileName: string,
): NonNullable<GenerationCustomComponent['importRef']> {
  const readAttribute = (key: string): string | undefined => {
    const value = layer.attributes?.[key];
    return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;
  };
  const fileName = readAttribute('importFileName')
    ?? readAttribute('cadSourceFile')
    ?? fallbackFileName;
  const layerName = readAttribute('cadLayer') ?? layer.folderPath.at(-1);
  const readNumberAttribute = (key: string): number | undefined => {
    const value = layer.attributes?.[key];
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
  };
  const sourceSha256 = readAttribute('cadSourceSha256');
  const entityHandle = readAttribute('cadEntityHandle');
  const entityType = readAttribute('cadEntityType');
  const dwgVersion = readAttribute('cadDwgVersion');
  const metresPerUnit = readNumberAttribute('cadMetersPerUnit');
  const crsCode = readAttribute('cadCrsCode');
  return {
    fileName,
    ...(layerName && layerName.trim() !== '' ? { layerName: layerName.trim() } : {}),
    ...(sourceSha256 ? { sourceSha256 } : {}),
    ...(entityHandle ? { entityHandle } : {}),
    ...(entityType ? { entityType } : {}),
    ...(dwgVersion ? { dwgVersion } : {}),
    ...(metresPerUnit != null ? { metresPerUnit } : {}),
    ...(crsCode ? { crsCode } : {}),
  };
}

function upsertByKey<T>(
  existing: readonly T[],
  incoming: readonly T[],
  keyFor: (value: T) => string,
): T[] {
  const incomingByKey = new Map<string, T>();
  for (const value of incoming) incomingByKey.set(keyFor(value), value);
  const incomingKeys = new Set(incomingByKey.keys());
  return [
    ...existing.filter(value => !incomingKeys.has(keyFor(value))),
    ...incomingByKey.values(),
  ];
}

/**
 * Compose every non-reference assignment into one deterministic layout patch.
 * IDs bind file content, CAD/feature identity, and assigned engineering role,
 * so retries upsert while a deliberate role change creates a distinct entity.
 */
export function applyStudioImportAssignments(
  layout: GenerationLayoutConfiguration,
  rows: readonly StudioImportRow[],
  anchor: { lat: number; lng: number },
  importId: string,
): StudioImportApplication {
  const referenceLayers: ParsedLayer[] = [];
  const referenceRowIndexes: number[] = [];
  const errors: string[] = [];
  const warnings: string[] = [];
  const placements: GenerationLayoutManualPlacement[] = [];
  const customs: GenerationCustomComponent[] = [];
  const barriers: GenerationAcousticBarrier[] = [];
  const zones: NonNullable<GenerationLayoutConfiguration['exclusions']>['zones'] = [];
  const assignments: GenerationImportedLayerAssignment[] = [];

  rows.forEach((row, rowIndex) => {
    const { layer, assignment } = row;
    const attributes = layer.attributes ?? {};
    const rowImportId = typeof attributes.importId === 'string'
      && attributes.importId.trim() !== ''
      ? attributes.importId.trim()
      : importId;
    const entityHandle = [
      attributes.cadEntityHandle,
      attributes.entityHandle,
      attributes.featureId,
      attributes.id,
    ].find(value => (
      (typeof value === 'string' && value.trim() !== '')
      || typeof value === 'number'
    ));
    const entityIdentity = entityHandle == null
      ? JSON.stringify({
          name: layer.name,
          folderPath: layer.folderPath,
          geometryType: layer.geometry.type,
          coordinates: layer.geometry.coordinates.map(point => [
            Number(point.lat.toFixed(9)),
            Number(point.lng.toFixed(9)),
          ]),
        })
      : String(entityHandle);
    // Role-INDEPENDENT source id: the hash binds file content + entity identity
    // but NOT the assigned role, so reclassifying a source keeps the same id and
    // the reconciler can find and remove its prior artifacts across collections.
    // Height and corridor width are editable parameters, not entity identity.
    const entityHash = sha256Hex(`${rowImportId}\n${entityIdentity}`);
    const id = `import-${rowImportId.slice(0, 16)}-${entityHash.slice(0, 16)}`;
    const origin = { kind: 'imported-layer-assignment' as const, assignmentId: id };
    const label = layer.name.trim() !== '' ? layer.name.trim() : id;
    const recordAssignment = (
      role: GenerationImportedLayerAssignment['role'],
      componentId?: string,
    ): void => {
      assignments.push({
        id,
        sourceLabel: label,
        sourceContentHash: entityHash,
        geometryType: layer.geometry.type,
        role,
        ...(componentId ? { componentId } : {}),
      });
    };
    const coordinates = layer.geometry.coordinates.filter(point => (
      Number.isFinite(point.lat)
      && Number.isFinite(point.lng)
      && point.lat >= -90
      && point.lat <= 90
      && point.lng >= -180
      && point.lng <= 180
    ));
    const openCoordinates = openRing(coordinates);
    const minimumVertices = layer.geometry.type === 'point'
      ? 1
      : layer.geometry.type === 'line' ? 2 : 3;
    if (
      coordinates.length !== layer.geometry.coordinates.length
      || openCoordinates.length < minimumVertices
    ) {
      errors.push(
        `${label}: ${layer.geometry.type} geometry needs ${minimumVertices} valid `
        + `${minimumVertices === 1 ? 'coordinate' : 'vertices'}.`,
      );
      return;
    }

    switch (assignment.role) {
      case 'reference':
        referenceLayers.push(layer);
        referenceRowIndexes.push(rowIndex);
        return;
      case 'exclusion': {
        const ring = openRing(coordinates);
        if (layer.geometry.type !== 'polygon' || ring.length < 3) {
          errors.push(`${label}: exclusion zones need a polygon with at least 3 vertices.`);
          return;
        }
        zones.push({
          id,
          label,
          source: 'site-layer',
          kind: 'custom',
          polygons: [ring.map(point => ({ ...point }))],
          importRef: importedComponentRef(layer, label),
          origin,
        });
        recordAssignment('exclusion');
        return;
      }
      case 'barrier': {
        const path = assignment.kind === 'building' ? openRing(coordinates) : [...coordinates];
        const minVertices = assignment.kind === 'building' ? 3 : 2;
        if (path.length < minVertices) {
          errors.push(`${label}: a ${assignment.kind} barrier needs at least ${minVertices} vertices.`);
          return;
        }
        if (assignment.heightM == null
          || !Number.isFinite(assignment.heightM)
          || assignment.heightM < MIN_BARRIER_HEIGHT_M
          || assignment.heightM > MAX_BARRIER_HEIGHT_M) {
          errors.push(`${label}: barrier height must be ${MIN_BARRIER_HEIGHT_M}–${MAX_BARRIER_HEIGHT_M} m.`);
          return;
        }
        barriers.push({
          id: `barrier-${id}`,
          label,
          kind: assignment.kind,
          path: path.map(point => ({ ...point })),
          heightM: assignment.heightM,
          importRef: importedComponentRef(layer, label),
          origin,
        });
        recordAssignment('barrier');
        return;
      }
      case 'custom': {
        if (layer.geometry.type === 'line') {
          if (assignment.kind !== 'road') {
            errors.push(`${label}: line geometry can only become a road corridor or a barrier.`);
            return;
          }
          if (assignment.corridorWidthM == null
            || !Number.isFinite(assignment.corridorWidthM)
            || assignment.corridorWidthM <= 0
            || assignment.corridorWidthM > 100) {
            errors.push(`${label}: road corridor width must be greater than 0 and at most 100 m.`);
            return;
          }
          if (coordinates.length < 2) {
            errors.push(`${label}: a road centerline needs at least 2 vertices.`);
            return;
          }
          customs.push({
            id,
            kind: 'road',
            label,
            shape: {
              type: 'polyline',
              path: coordinates.map(point => ({ ...point })),
              widthM: assignment.corridorWidthM,
            },
            lifecycle: 'permanent',
            source: 'imported',
            importRef: importedComponentRef(layer, label),
            origin,
          });
          recordAssignment('custom');
          return;
        }
        const ring = openRing(coordinates);
        if (ring.length < 3) {
          errors.push(`${label}: a custom footprint needs a polygon with at least 3 vertices.`);
          return;
        }
        customs.push({
          id,
          kind: assignment.kind,
          label,
          shape: { type: 'polygon', path: ring.map(point => ({ ...point })) },
          lifecycle: 'permanent',
          source: 'imported',
          importRef: importedComponentRef(layer, label),
          origin,
        });
        recordAssignment('custom');
        return;
      }
      case 'equipment': {
        if (assignment.componentId.trim() === '') {
          errors.push(`${label}: select equipment to pin.`);
          return;
        }
        // A point supplies the centre only; orientation defaults to the layout
        // axis (editable later). Modeled equipment keeps its own dimensions.
        if (layer.geometry.type === 'point') {
          const point = coordinates[0]!;
          const pointLocal = localAxesForPoint(point, anchor, layout.rotationDeg);
          placements.push({
            componentId: assignment.componentId,
            xM: pointLocal.xM,
            yM: pointLocal.yM,
            rotationDeg: 0,
            origin,
          });
          recordAssignment('equipment', assignment.componentId);
          return;
        }
        const ring = openRing(coordinates);
        if (layer.geometry.type !== 'polygon' || ring.length < 3) {
          errors.push(`${label}: equipment pins need a polygon footprint or a point centre.`);
          return;
        }
        // Modeled equipment supplies dimensions; the imported polygon supplies
        // POSE ONLY. Centroid = shoelace area centroid; orientation = the
        // minimum-area-rectangle major axis. Near-square sources have no stable
        // major axis, so they fall back to the layout axis with a warning.
        const centroid = polygonCentroidLatLng(ring, anchor);
        if (!centroid) {
          errors.push(`${label}: could not derive a centroid for the equipment pin.`);
          return;
        }
        const local = localAxesForPoint(centroid, anchor, layout.rotationDeg);
        const rect = minimumAreaRectangle(ring, anchor);
        const nearSquare = rect != null && rect.aspectRatio <= NEAR_SQUARE_ASPECT;
        if (nearSquare) {
          warnings.push(`${label}: near-square source (aspect ${rect!.aspectRatio.toFixed(2)}) — orientation aligned to the layout axis; set a bearing if needed.`);
        }
        // Placement rotationDeg is relative to the layout grid; a world-axis
        // MAR bearing minus the grid rotation gives the local rotation.
        const worldBearing = rect && !nearSquare ? rect.bearingDeg : layout.rotationDeg;
        placements.push({
          componentId: assignment.componentId,
          xM: local.xM,
          yM: local.yM,
          rotationDeg: worldBearing - layout.rotationDeg,
          origin,
        });
        recordAssignment('equipment', assignment.componentId);
        return;
      }
    }
  });

  // The import dialog is one transaction from the user's point of view:
  // validation errors leave both layout and reference collections untouched.
  if (errors.length > 0) {
    return { patch: null, referenceLayers, referenceRowIndexes, errors, warnings };
  }

  if (assignments.length === 0) {
    return { patch: null, referenceLayers, referenceRowIndexes, errors, warnings };
  }

  // Reconcile by ownership: every artifact a re-assigned source previously
  // owned (any collection) is removed BEFORE the new ones land, so reclassifying
  // a source never leaves orphan geometry. The source id is role-independent, so
  // an existing artifact and its replacement share an `origin.assignmentId`.
  const incomingIds = new Set(assignments.map(assignment => assignment.id));
  const ownedByIncoming = (artifact: { origin?: { assignmentId: string } }): boolean =>
    artifact.origin != null && incomingIds.has(artifact.origin.assignmentId);
  const reconcile = <T extends { origin?: { assignmentId: string } }>(
    existing: readonly T[],
    incoming: readonly T[],
    keyFor: (value: T) => string,
  ): T[] => upsertByKey(existing.filter(value => !ownedByIncoming(value)), incoming, keyFor);
  // Emit a collection when it gains new artifacts OR loses re-assigned ones.
  const changed = <T extends { origin?: { assignmentId: string } }>(
    existing: readonly T[],
    incoming: readonly T[],
  ): boolean => incoming.length > 0 || existing.some(ownedByIncoming);

  const existingPlacements = layout.manualPlacements ?? [];
  const existingCustoms = layout.customComponents ?? [];
  const existingBarriers = layout.acousticBarriers ?? [];
  const existingZones = layout.exclusions?.zones ?? [];

  const patch: Partial<GenerationLayoutConfiguration> = { schemaVersion: 2 };
  if (changed(existingPlacements, placements)) {
    patch.manualPlacements = reconcile(existingPlacements, placements, placement => placement.componentId);
  }
  if (changed(existingCustoms, customs)) {
    patch.customComponents = reconcile(existingCustoms, customs, component => component.id);
  }
  if (changed(existingBarriers, barriers)) {
    patch.acousticBarriers = reconcile(existingBarriers, barriers, barrier => barrier.id);
  }
  if (changed(existingZones, zones)) {
    const nextZones = reconcile(existingZones, zones, zone => zone.id);
    patch.exclusions = layout.exclusions
      ? { ...layout.exclusions, zones: nextZones }
      : { zones: nextZones, femaStatus: 'not-fetched' as const };
  }
  patch.importedAssignments = [
    ...(layout.importedAssignments ?? []).filter(assignment => !incomingIds.has(assignment.id)),
    ...assignments,
  ];
  return { patch, referenceLayers, referenceRowIndexes, errors, warnings };
}
